// Letture Firestore con timeout e riapertura della connessione.
//
// Perché serve: la get() dell'SDK non ha timeout. Su iOS Safari, dopo che il
// tab è stato sospeso in background, il canale WebChannel può restare morto
// mentre l'SDK si crede ancora *Online*: una get() aspetta allora all'infinito
// (il timeout di 10s dell'SDK vale solo finché lo stato è *Unknown*). Il sito
// restava su "Caricamento…" finché l'utente non ricaricava la pagina.
//
// Rimedio: se la lettura non si chiude entro `timeoutMs`, forziamo l'SDK a
// riaprire la connessione (disableNetwork + enableNetwork) e rileggiamo una
// volta sola. Se fallisce anche il secondo tentativo, l'errore sale al
// chiamante, che mostra lo stato d'errore con "Riprova".

import firebase from 'firebase/compat/app';

export const READ_TIMEOUT_MS = 10000;

class ReadTimeoutError extends Error {
    constructor(timeoutMs) {
        super(`Firestore read timeout after ${timeoutMs}ms`);
        this.name = 'ReadTimeoutError';
    }
}

// Corre la lettura contro un timer; il timer viene sempre ripulito, sia che
// vinca la lettura sia che vinca il timeout.
export function withTimeout(readFn, timeoutMs) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new ReadTimeoutError(timeoutMs)), timeoutMs);
    });
    // Promise.resolve().then: anche un readFn che lancia in modo sincrono
    // diventa una promise rifiutata.
    const read = Promise.resolve().then(readFn);
    return Promise.race([read, timeout]).finally(() => clearTimeout(timer));
}

// Vale la pena riaprire la connessione solo se la lettura si è appesa o se
// l'SDK si dichiara offline ('unavailable', tipico di una get() dal server);
// un permission-denied o un bug non si curano riconnettendo.
function isConnectionProblem(error) {
    return error instanceof ReadTimeoutError || (error && error.code === 'unavailable');
}

export async function readWithReconnect(readFn, { timeoutMs = READ_TIMEOUT_MS, db = firebase.firestore() } = {}) {
    try {
        return await withTimeout(readFn, timeoutMs);
    } catch (error) {
        if (!isConnectionProblem(error)) throw error;
    }

    // Da qui il primo tentativo è abbandonato: la race ha già chiuso, quindi
    // il suo esito non viene più letto. È voluto — disableNetwork sblocca la
    // get() appesa con lo snapshot VUOTO della cache, che non va mai usato.
    await db.disableNetwork();
    await db.enableNetwork();

    return withTimeout(readFn, timeoutMs);
}
